/** Mirror of `functions/shared/grant-view.ts`. Kept as a copy intentionally — the UI doesn't
 *  share a TS project with the Lambda. Update both if the schema changes.
 */
export type GrantSource = 'iam-bastion' | 'access-ops';

/**
 * Impersonation is per application: Ennabl AI and Decisions are separate
 * capabilities, and a wildcard for one never implies the other.
 *
 * The UI only ever sees this DOMAIN vocabulary. Decisions impersonation is
 * stored as `impersonation` in DynamoDB and translated in the Lambda
 * (`grantFromRow`), for reasons documented there — nothing in the UI should
 * spell the stored value.
 */
export type ImpersonationKind = 'imp-ai' | 'imp-decisions';

export type GrantKind = 'data-access' | 'data-access-temporary' | ImpersonationKind;

/** True for either impersonation kind — never for data access. */
export function isImpersonationKind(kind: GrantKind): kind is ImpersonationKind {
    return kind === 'imp-ai' || kind === 'imp-decisions';
}

/** The application an impersonation kind belongs to, for headings and labels. */
export const APP_LABEL: Record<ImpersonationKind, string> = {
    'imp-ai': 'Ennabl AI',
    'imp-decisions': 'Decisions',
};

export interface GrantView {
    id: string;
    source: GrantSource;
    email: string;
    resource: string;
    /** Human-readable name for resource, looked up server-side from shard config. */
    resourceName?: string;
    kind: GrantKind;
    expiresAt?: string;
    raw?: Record<string, unknown>;
}
